import React, { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { SunburstChart } from "echarts/charts";
import {
  TooltipComponent,
  TitleComponent,
  LegendComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { DEMAND_NOTICE } from "./TarifUtils"; // adjust path if needed

echarts.use([
  SunburstChart,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  CanvasRenderer,
]);

const darkGreyColors = ['#333333', '#444444', '#555555', '#222222'];
const blueColors = ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#2196f3', '#00bcd4', '#4fc3f7', '#81d4fa'];

// Sunburst data in the style of the ECharts "drink" example
const getSunburstData = (seasonData) => {
  if (!seasonData || seasonData.length === 0) return [];
  return seasonData.map((season, sIdx) => ({
    name: season.name,
    label: { show: true },
    itemStyle: { color: darkGreyColors[sIdx % darkGreyColors.length] },
    children: season.periods.map((period, pIdx) => ({
      name: period.demandTypeName,
      label: { show: true },
      itemStyle: { color: darkGreyColors[(sIdx + pIdx + 1) % darkGreyColors.length] },
      children: period.rate.map((rate, rIdx) => ({
        name: rate.name || `Period ${rate.tariffId}`,
        label: { show: true },
        itemStyle: { color: blueColors[(rIdx * 2) % blueColors.length] },
        children: rate.options.tierBoundaries.flatMap((tier) =>
          Object.keys(tier)
            .filter((key) => key !== "tierId")
            .map((key, dIdx) => ({
              name: DEMAND_NOTICE[key]?.name || `Notice ${key}`,
              value: 1,
              label: { show: true },
              itemStyle: { color: blueColors[(rIdx * 2 + 1) % blueColors.length] },
            }))
        ),
      })),
    })),
  }));
};

const SeasonSunburstChart = ({ seasonData }) => {
  const chartRef = useRef(null);
  const data = getSunburstData(seasonData);
  useEffect(() => {
    let chartInstance;
    if (chartRef.current && data.length > 0) {
      chartInstance = echarts.init(chartRef.current);
    //   chartInstance.clear();
      const option = {
         series: [
          {
            type: "sunburst",
            data,
            radius: [0, "95%"],
            label: {
              rotate: "radial",
              color: '#fff',
            },
            levels: [
              {},
              {
                r0: "0%",
                r: "28%",
                label: { rotate: 0, fontWeight: 700, fontSize: 16, color: '#fff' },
              },
              {
                r0: "28%",
                r: "55%",
                label: { fontWeight: 600, fontSize: 14, color: '#fff' },
              },
              {
                r0: "55%",
                r: "80%",
                label: { fontWeight: 500, fontSize: 12, color: '#fff' },
              },
              {
                r0: "80%",
                r: "95%",
                label: { fontWeight: 400,   position: 'outside',fontSize: 11, color: '#fff' },
              },
            ],
          },
        ],
        tooltip: {
          trigger: "item",
          backgroundColor: '#222',
          borderColor: '#444',
          textStyle: { color: '#fff' },
          formatter: (params) => params.name,
        },
      };
      chartInstance.setOption(option);
      // Responsive resize
      const handleResize = () => chartInstance.resize();
      window.addEventListener("resize", handleResize);
      // Force resize after mount in case container is hidden initially
      setTimeout(() => chartInstance.resize(), 300);
      return () => {
        window.removeEventListener("resize", handleResize);
        // chartInstance.dispose();
      };
    }
  }, [data]);

  // Debug: log data to console
  useEffect(() => {
    if (!chartRef.current) {
      console.warn("Chart ref is not attached to a DOM node.");
    }
    if (!data || data.length === 0) {
      console.warn("No data provided to sunburst chart.");
    }
  }, [data]);

  if (!data || data.length === 0) {
    return <div style={{ padding: 24, color: 'red' }}>No data available for sunburst chart.</div>;
  }

  return (
    <div
      ref={chartRef}
      style={{ width: "100%", minWidth: 700, maxWidth: 900, height: 500, minHeight: 450, zoom: "118%", margin: '0 auto' }}
    />
  );
};

export default SeasonSunburstChart;
